import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import { getStoresPaginatedApi, StorePublic } from "../../store/storeApi";
import {
  createHolidayApi,
  deleteHolidayApi,
  DpHoliday,
  DpHolidayScope,
  HOLIDAY_SCOPE_LABELS,
  listHolidaysApi,
  seedNationalHolidaysApi,
} from "../api/scheduleApi";
import { formatDateOnly } from "../utils/format";
import EcoConfirmDialog from "../../../components/eco-ui/EcoConfirmDialog";

const UFS = ["RJ", "SP", "MG", "ES"];
const emptyForm = { date: "", name: "", scope: "ESTADUAL" as DpHolidayScope, state: "RJ", storeId: "" };

/**
 * Aba "Feriados" em Cadastros. Os nacionais o sistema carrega por ano; estaduais
 * (por UF) e municipais (por loja) o DP cadastra. É a fonte de feriado da folha
 * (dias de VT/VR, reflexo de DSR, 5º dia útil) e da escala da loja.
 */
export default function HolidaysSettings({ token }: { token: string }) {
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [items, setItems] = useState<DpHoliday[]>([]);
  const [stores, setStores] = useState<StorePublic[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [removeTarget, setRemoveTarget] = useState<DpHoliday | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await listHolidaysApi(token, year));
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar os feriados.");
    } finally {
      setLoading(false);
    }
  }, [token, year]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    getStoresPaginatedApi(token, { all: true })
      .then((res) => setStores(res.items ?? []))
      .catch(() => setStores([]));
  }, [token]);

  const seed = async () => {
    setSaving(true);
    try {
      const result = await seedNationalHolidaysApi(token, year);
      toast.success(
        result.created
          ? `${result.created} feriado(s) nacional(is) de ${year} carregado(s).`
          : `Os feriados nacionais de ${year} já estavam carregados.`,
      );
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar os feriados nacionais.");
    } finally {
      setSaving(false);
    }
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.date) return toast.error("Informe a data.");
    if (!form.name.trim()) return toast.error("Informe o nome do feriado.");
    if (form.scope === "MUNICIPAL" && !form.storeId) return toast.error("Escolha a loja do feriado municipal.");
    setSaving(true);
    try {
      await createHolidayApi(token, {
        date: form.date,
        name: form.name.trim(),
        scope: form.scope,
        state: form.scope === "ESTADUAL" ? form.state : undefined,
        storeId: form.scope === "MUNICIPAL" ? form.storeId : undefined,
      });
      toast.success("Feriado cadastrado.");
      setModal(false);
      setForm(emptyForm);
      if (Number(form.date.slice(0, 4)) !== year) setYear(Number(form.date.slice(0, 4)));
      else await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao cadastrar o feriado.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (holiday: DpHoliday) => {
    setSaving(true);
    try {
      await deleteHolidayApi(token, holiday.id);
      toast.success("Feriado excluído.");
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao excluir o feriado.");
    } finally {
      setSaving(false);
      setRemoveTarget(null);
    }
  };

  const where = (h: DpHoliday) =>
    h.scope === "NACIONAL" ? "Todo o país" : h.scope === "ESTADUAL" ? h.state ?? "—" : h.store?.nomeFantasia ?? "—";

  return (
    <>
      <div className="dpControls" style={{ flexWrap: "wrap", gap: 8 }}>
        <div className="dpFormGroup" style={{ margin: 0, minWidth: 120 }}>
          <label htmlFor="holidayYear">Ano</label>
          <select id="holidayYear" className="dpSelect" value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {[year - 1, year, year + 1, year + 2]
              .filter((y, i, arr) => arr.indexOf(y) === i)
              .map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
          </select>
        </div>
        <button className="dpBtn dpBtn--secondary" onClick={seed} disabled={saving}>
          Carregar nacionais de {year}
        </button>
        <button className="dpBtn dpBtn--primary" onClick={() => setModal(true)} disabled={saving}>
          Novo feriado
        </button>
      </div>
      <p className="dpFormHint" style={{ marginBottom: 12 }}>
        Os nacionais vêm prontos (inclui a Sexta-feira Santa; Carnaval e Corpus Christi são ponto facultativo e não entram).
        Cadastre os estaduais por UF e os municipais por loja. A folha e a escala da loja usam só os feriados que valem para
        a loja do colaborador.
      </p>

      {loading ? (
        <div className="dpEmpty">Carregando...</div>
      ) : (
        <div className="dpTableWrapper">
          <table className="dpTable">
            <thead>
              <tr>
                <th>Data</th>
                <th>Feriado</th>
                <th>Tipo</th>
                <th>Onde vale</th>
                <th style={{ width: 90 }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="dpEmpty">
                    Nenhum feriado em {year}. Use "Carregar nacionais de {year}".
                  </td>
                </tr>
              ) : (
                items.map((h) => (
                  <tr key={h.id}>
                    <td>{formatDateOnly(h.date)}</td>
                    <td>{h.name}</td>
                    <td>
                      <span className={`dpBadge ${h.scope === "NACIONAL" ? "dpBadge--blue" : h.scope === "ESTADUAL" ? "dpBadge--green" : "dpBadge--orange"}`}>
                        {HOLIDAY_SCOPE_LABELS[h.scope]}
                      </span>
                    </td>
                    <td>{where(h)}</td>
                    <td>
                      <div className="dpTable__actions">
                        <button className="dpBtn dpBtn--danger dpBtn--small" onClick={() => setRemoveTarget(h)}>
                          Excluir
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <EcoConfirmDialog
        open={Boolean(removeTarget)}
        title="Excluir feriado?"
        message={
          removeTarget ? (
            <>
              <strong>{removeTarget.name}</strong> ({formatDateOnly(removeTarget.date)}) deixa de contar na folha e na escala.
            </>
          ) : null
        }
        confirmLabel="Excluir"
        tone="danger"
        busy={saving}
        onConfirm={() => removeTarget && remove(removeTarget)}
        onCancel={() => setRemoveTarget(null)}
      />

      {modal ? (
        <div className="dpModalOverlay">
          <form className="dpModal" onSubmit={save}>
            <header className="dpModal__header">
              <h2 className="dpModal__title">Novo feriado</h2>
              <button type="button" className="dpModal__close" onClick={() => setModal(false)}>
                &times;
              </button>
            </header>
            <div className="dpModal__body">
              <div className="dpFormGrid">
                <div className="dpFormGroup">
                  <label htmlFor="holidayDate">Data *</label>
                  <input id="holidayDate" type="date" className="dpInput" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
                </div>
                <div className="dpFormGroup">
                  <label htmlFor="holidayScope">Tipo *</label>
                  <select
                    id="holidayScope"
                    className="dpSelect"
                    value={form.scope}
                    onChange={(e) => setForm({ ...form, scope: e.target.value as DpHolidayScope })}
                  >
                    {(Object.keys(HOLIDAY_SCOPE_LABELS) as DpHolidayScope[]).map((scope) => (
                      <option key={scope} value={scope}>
                        {HOLIDAY_SCOPE_LABELS[scope]}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="dpFormGroup" style={{ gridColumn: "1 / -1" }}>
                  <label htmlFor="holidayName">Nome *</label>
                  <input
                    id="holidayName"
                    className="dpInput"
                    maxLength={120}
                    placeholder="Ex.: São Jorge"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>
                {form.scope === "ESTADUAL" ? (
                  <div className="dpFormGroup">
                    <label htmlFor="holidayState">UF *</label>
                    <select id="holidayState" className="dpSelect" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })}>
                      {UFS.map((uf) => (
                        <option key={uf} value={uf}>
                          {uf}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}
                {form.scope === "MUNICIPAL" ? (
                  <div className="dpFormGroup" style={{ gridColumn: "1 / -1" }}>
                    <label htmlFor="holidayStore">Loja *</label>
                    <select id="holidayStore" className="dpSelect" value={form.storeId} onChange={(e) => setForm({ ...form, storeId: e.target.value })}>
                      <option value="">Escolha a loja</option>
                      {stores.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.nomeFantasia}
                        </option>
                      ))}
                    </select>
                    <span className="dpFormHint">O feriado municipal vale para a loja escolhida.</span>
                  </div>
                ) : null}
              </div>
            </div>
            <footer className="dpModal__footer">
              <button type="button" className="dpBtn dpBtn--secondary" onClick={() => setModal(false)} disabled={saving}>
                Cancelar
              </button>
              <button type="submit" className="dpBtn dpBtn--primary" disabled={saving}>
                Cadastrar
              </button>
            </footer>
          </form>
        </div>
      ) : null}
    </>
  );
}
